import React, { useState, useEffect } from 'react';
import { Button } from './ui/button';
import { Input } from './ui/input';
import { Label } from './ui/label';
import { Card, CardContent } from './ui/card';
import { Calendar, Clock, Phone, User, ChevronLeft, ChevronRight, Check, Loader2 } from 'lucide-react';
import api from '../services/api';
import { toast } from 'sonner';

const ConsultationScheduler = ({ athleteName, readinessZone, onBookingComplete }) => {
  const [loading, setLoading] = useState(true);
  const [availableDays, setAvailableDays] = useState([]);
  const [selectedDay, setSelectedDay] = useState(null);
  const [selectedSlot, setSelectedSlot] = useState(null);
  const [step, setStep] = useState('calendar'); // 'calendar', 'form', 'success'
  const [bookingLoading, setBookingLoading] = useState(false);
  const [bookingDetails, setBookingDetails] = useState(null);
  
  const [formData, setFormData] = useState({
    parentName: '',
    parentEmail: '',
    parentPhone: ''
  });

  // Fetch available slots on mount
  useEffect(() => {
    fetchAvailableSlots();
  }, []);

  const fetchAvailableSlots = async () => {
    try {
      setLoading(true);
      const response = await api.get('/academy/consultations/available-slots');
      if (response.data.success) {
        setAvailableDays(response.data.available_days);
        // Auto-select first available day
        if (response.data.available_days.length > 0) {
          setSelectedDay(response.data.available_days[0]);
        }
      }
    } catch (error) {
      console.error('Error fetching slots:', error);
      toast.error('Failed to load available times');
    } finally {
      setLoading(false);
    }
  };

  const handleSlotSelect = (slot) => {
    setSelectedSlot(slot);
  };

  const handleContinueToForm = () => {
    if (!selectedSlot) {
      toast.error('Please select a time slot');
      return;
    }
    setStep('form');
  };

  const handleBooking = async () => {
    if (!formData.parentName || !formData.parentEmail || !formData.parentPhone) {
      toast.error('Please fill in all fields');
      return;
    }

    // Basic phone validation
    const phoneRegex = /^[\d\s\-\+\(\)]{10,}$/;
    if (!phoneRegex.test(formData.parentPhone)) {
      toast.error('Please enter a valid phone number');
      return;
    }

    setBookingLoading(true);
    try {
      const response = await api.post('/academy/consultations/book', {
        athlete_name: athleteName,
        parent_name: formData.parentName,
        parent_email: formData.parentEmail,
        parent_phone: formData.parentPhone,
        selected_date: selectedDay.date,
        selected_time: selectedSlot.time,
        readiness_zone: readinessZone
      });

      if (response.data.success) {
        setBookingDetails(response.data);
        setStep('success');
        toast.success('Consultation booked successfully!');
        if (onBookingComplete) {
          onBookingComplete(response.data);
        }
      }
    } catch (error) {
      console.error('Error booking consultation:', error);
      const message = error.response?.data?.detail || 'Failed to book consultation';
      toast.error(message);
      // Refresh slots in case one was taken
      fetchAvailableSlots();
    } finally {
      setBookingLoading(false);
    }
  };

  if (loading) {
    return (
      <div className="flex items-center justify-center py-12">
        <Loader2 className="w-8 h-8 animate-spin text-orange-600" />
        <span className="ml-3 text-gray-400">Loading available times...</span>
      </div>
    );
  }

  // Success state
  if (step === 'success' && bookingDetails) {
    return (
      <div className="text-center py-8">
        <div className="inline-flex items-center justify-center w-16 h-16 rounded-full bg-green-600/20 mb-6">
          <Check className="w-8 h-8 text-green-500" />
        </div>
        
        <h3 className="text-2xl font-black text-white mb-2">Consultation Booked!</h3>
        <p className="text-gray-400 mb-6">
          Check your email for the meeting link and calendar invite.
        </p>
        
        <div className="bg-white/5 border border-gray-800 rounded-lg p-6 max-w-sm mx-auto">
          <div className="text-orange-600 text-lg font-bold mb-2">
            {bookingDetails.details.date_display}
          </div>
          <div className="text-3xl font-black text-white mb-4">
            {bookingDetails.details.time_display}
          </div>
          <div className="text-sm text-gray-400">
            Meeting link sent to {formData.parentEmail}
          </div>
        </div>
      </div>
    );
  }

  // Form step
  if (step === 'form') {
    return (
      <div className="max-w-md mx-auto">
        <button 
          onClick={() => setStep('calendar')}
          className="flex items-center text-gray-400 hover:text-white mb-6 transition-colors"
        >
          <ChevronLeft className="w-4 h-4 mr-1" />
          Back to calendar
        </button>
        
        <div className="bg-white/5 border border-orange-600/30 rounded-lg p-4 mb-6">
          <div className="flex items-center justify-between">
            <div>
              <div className="text-sm text-gray-400">Selected Time</div>
              <div className="text-white font-bold">{selectedDay.day_name}, {selectedDay.day_display}</div>
            </div>
            <div className="text-right">
              <div className="text-2xl font-black text-orange-600">{selectedSlot.display}</div>
            </div>
          </div>
        </div>

        <h3 className="text-xl font-bold text-white mb-4">Parent/Guardian Information</h3>
        
        <div className="space-y-4">
          <div>
            <Label className="text-white mb-2 block flex items-center gap-2">
              <User className="w-4 h-4 text-orange-600" />
              Your Name
            </Label>
            <Input 
              value={formData.parentName}
              onChange={(e) => setFormData({...formData, parentName: e.target.value})}
              placeholder="Enter your full name"
              className="bg-white/10 border-gray-700 text-white placeholder:text-gray-500"
            />
          </div>

          <div>
            <Label className="text-white mb-2 block flex items-center gap-2">
              <span className="text-orange-600">@</span>
              Email Address
            </Label>
            <Input 
              type="email"
              value={formData.parentEmail}
              onChange={(e) => setFormData({...formData, parentEmail: e.target.value})}
              placeholder="your@email.com"
              className="bg-white/10 border-gray-700 text-white placeholder:text-gray-500"
            />
          </div>

          <div>
            <Label className="text-white mb-2 block flex items-center gap-2">
              <Phone className="w-4 h-4 text-orange-600" />
              Phone Number
            </Label>
            <Input 
              type="tel"
              value={formData.parentPhone}
              onChange={(e) => setFormData({...formData, parentPhone: e.target.value})}
              placeholder="(555) 123-4567"
              className="bg-white/10 border-gray-700 text-white placeholder:text-gray-500"
            />
          </div>

          <Button 
            onClick={handleBooking}
            disabled={bookingLoading}
            className="w-full bg-orange-600 hover:bg-orange-700 text-white font-black text-lg py-6 rounded-none mt-6"
          >
            {bookingLoading ? (
              <>
                <Loader2 className="w-5 h-5 mr-2 animate-spin" />
                Booking...
              </>
            ) : (
              <>
                <Check className="w-5 h-5 mr-2" />
                Confirm Booking
              </>
            )}
          </Button>
        </div>
      </div>
    );
  }

  // Calendar step
  return (
    <div>
      <h3 className="text-xl font-bold text-white mb-2 flex items-center gap-2">
        <Calendar className="w-5 h-5 text-orange-600" />
        Select a Day
      </h3>
      <p className="text-gray-400 text-sm mb-6">Choose a consultation time that works for you</p>

      {/* Day selector */}
      <div className="flex gap-2 overflow-x-auto pb-4 mb-6 scrollbar-hide">
        {availableDays.map((day) => (
          <button
            key={day.date}
            onClick={() => {
              setSelectedDay(day);
              setSelectedSlot(null);
            }}
            className={`flex-shrink-0 p-4 rounded-lg border-2 transition-all min-w-[100px] text-center ${
              selectedDay?.date === day.date
                ? 'border-orange-600 bg-orange-600/10'
                : 'border-gray-700 hover:border-gray-600 bg-white/5'
            }`}
          >
            <div className={`text-xs uppercase tracking-wider mb-1 ${
              selectedDay?.date === day.date ? 'text-orange-600' : 'text-gray-500'
            }`}>
              {day.day_name.slice(0, 3)}
            </div>
            <div className={`text-lg font-bold ${
              selectedDay?.date === day.date ? 'text-white' : 'text-gray-300'
            }`}>
              {day.day_display}
            </div>
            <div className="text-xs text-gray-500 mt-1">
              {day.slots.length} slots
            </div>
          </button>
        ))}
      </div>

      {/* Time slots */}
      {selectedDay && (
        <>
          <h4 className="text-lg font-bold text-white mb-3 flex items-center gap-2">
            <Clock className="w-4 h-4 text-orange-600" />
            Available Times
          </h4>
          <div className="grid grid-cols-3 sm:grid-cols-4 gap-2 mb-6">
            {selectedDay.slots.map((slot) => (
              <button
                key={slot.time}
                onClick={() => handleSlotSelect(slot)}
                className={`p-3 rounded-lg border-2 transition-all text-center ${
                  selectedSlot?.time === slot.time
                    ? 'border-orange-600 bg-orange-600 text-white'
                    : 'border-gray-700 hover:border-gray-500 bg-white/5 text-gray-300 hover:text-white'
                }`}
              >
                <span className="font-bold">{slot.display}</span>
              </button>
            ))}
          </div>

          <Button 
            onClick={handleContinueToForm}
            disabled={!selectedSlot}
            className={`w-full font-black text-lg py-6 rounded-none transition-all ${
              selectedSlot 
                ? 'bg-orange-600 hover:bg-orange-700 text-white' 
                : 'bg-gray-700 text-gray-400 cursor-not-allowed'
            }`}
          >
            Continue
            <ChevronRight className="w-5 h-5 ml-2" />
          </Button>
        </>
      )}

      {availableDays.length === 0 && (
        <div className="text-center py-8">
          <div className="text-gray-400 mb-4">No available slots at the moment.</div>
          <Button 
            onClick={fetchAvailableSlots}
            variant="outline"
            className="border-gray-600 text-gray-300"
          >
            Refresh
          </Button>
        </div>
      )}
    </div>
  );
};

export default ConsultationScheduler;
